/**
 * ptキャンペーンの掲示ストア
 * 全画面で1つの掲示を共有します。画面が見えている間だけ取得し、Realtimeは使いません。
 * 取得したサーバー時刻は serverClock の基準にします。
 */

import { fetchPointCampaignBoard, type PointCampaignBoardItem } from './pointCampaignBoardService';
import { getServerClockEpoch, syncServerClock } from './serverClock';
import { logError } from '../utils/logger';

/** 掲示があるときの取得間隔 */
export const POINT_CAMPAIGN_BOARD_ACTIVE_INTERVAL_MS = 60 * 1000;
/** 掲示が無いときの取得間隔 */
export const POINT_CAMPAIGN_BOARD_IDLE_INTERVAL_MS = 2 * 60 * 1000;
/** RPCが未適用と分かってから、もう一度試すまでの時間 */
export const POINT_CAMPAIGN_BOARD_UNAVAILABLE_PAUSE_MS = 5 * 60 * 1000;
/** 間隔のばらつき（±20%）。多数の端末の取得が同じ瞬間にそろうのを避ける */
const JITTER_RATIO = 0.2;

/** 掲示のスナップショット */
export type PointCampaignBoardSnapshot = Readonly<{
  items: ReadonlyArray<PointCampaignBoardItem>;
}>;

/** 掲示が無い状態 */
const EMPTY_SNAPSHOT: PointCampaignBoardSnapshot = Object.freeze({ items: Object.freeze([]) });

/** 全画面で共有する掲示 */
let snapshot: PointCampaignBoardSnapshot = EMPTY_SNAPSHOT;
/** Reactの購読先 */
const listeners = new Set<() => void>();
/** 最後の購読解除時の停止処理 */
let stopSync: (() => void) | undefined;
/** StrictModeや画面遷移の即時再購読では同じ同期を維持する */
let stopTimer: ReturnType<typeof setTimeout> | undefined;
/** 次に取得する時刻（performance.now 基準）。画面遷移で同期を開き直しても、間隔より早く取り直さない */
let nextFetchAtMs = 0;
/** RPCが未適用と分かった後、問い合わせない期限（performance.now 基準） */
let unavailableUntilMs = 0;

/**
 * 間隔へ±20%のばらつきを加える
 * @param {number} intervalMs - 基準の間隔
 * @returns {number} ばらつきを加えた間隔
 */
const withJitter = (intervalMs: number): number => (
  intervalMs * (1 - JITTER_RATIO + Math.random() * JITTER_RATIO * 2)
);

/**
 * 掲示があるかで決まる次の取得までの間隔を返す
 * @returns {number} 取得間隔
 */
const resolveNextIntervalMs = (): number => withJitter(
  snapshot.items.length > 0 ? POINT_CAMPAIGN_BOARD_ACTIVE_INTERVAL_MS : POINT_CAMPAIGN_BOARD_IDLE_INTERVAL_MS,
);

/**
 * 取得結果を掲示へ反映する。内容が同じなら参照を保ち通知しない
 * @param {ReadonlyArray<PointCampaignBoardItem>} items - 取得した掲示
 * @returns {void}
 */
const applyItems = (items: ReadonlyArray<PointCampaignBoardItem>): void => {
  if (JSON.stringify(items) === JSON.stringify(snapshot.items)) return;
  snapshot = items.length > 0 ? Object.freeze({ items }) : EMPTY_SNAPSHOT;
  listeners.forEach((listener) => listener());
};

/**
 * 掲示の同一参照スナップショットを返す
 * @returns {PointCampaignBoardSnapshot} 掲示
 */
export const getPointCampaignBoardSnapshot = (): PointCampaignBoardSnapshot => snapshot;

/**
 * 掲示の定期取得を始める
 * @returns {() => void} タイマーと監視の停止処理
 */
export const startPointCampaignBoardSync = (): (() => void) => {
  /** この同期の生存状態 */
  let isActive = true;
  /** 取得の重複を抑止する */
  let isFetching = false;
  /** 次の取得のタイマー */
  let timer: ReturnType<typeof setTimeout> | undefined;

  /**
   * 次の取得を予約する
   * @param {number} delayMs - 待つ時間
   * @returns {void}
   */
  const schedule = (delayMs: number): void => {
    clearTimeout(timer);
    nextFetchAtMs = performance.now() + delayMs;
    timer = setTimeout(() => { void refresh(); }, delayMs);
  };

  /**
   * 見えていてオンラインのときだけ掲示を取得し、次を予約する。見えていない間は予約せず、復帰を待つ
   * @returns {Promise<void>}
   */
  const refresh = async (): Promise<void> => {
    if (!isActive || isFetching || document.visibilityState === 'hidden' || navigator.onLine === false) return;
    /** 未適用と分かってから試し直すまでの残り */
    const unavailablePauseMs = unavailableUntilMs - performance.now();
    if (unavailablePauseMs > 0) {
      schedule(unavailablePauseMs);
      return;
    }
    isFetching = true;
    clearTimeout(timer);
    /** 次の取得までの間隔。失敗は前の掲示を保ち、掲示の有無で決まる間隔で取り直す */
    let nextDelayMs: number | null = null;
    /** 取得を始めた時の時刻の版 */
    const clockEpoch = getServerClockEpoch();
    try {
      /** 取得結果 */
      const result = await fetchPointCampaignBoard();
      if (result.kind === 'unavailable') {
        unavailableUntilMs = performance.now() + POINT_CAMPAIGN_BOARD_UNAVAILABLE_PAUSE_MS;
        nextDelayMs = POINT_CAMPAIGN_BOARD_UNAVAILABLE_PAUSE_MS;
        applyItems([]);
      } else {
        syncServerClock(result.serverTime, clockEpoch);
        applyItems(result.items);
        // 取得中に画面を離れていたら、この応答の時刻は使えない。復帰の取得はこの取得と重なって止まったので、ここで取り直す。
        if (clockEpoch !== getServerClockEpoch()) nextDelayMs = 0;
      }
    } catch (error) {
      logError('ptキャンペーンの掲示を取得できませんでした。最後に取得した掲示を保持します。', {
        errorMessage: error instanceof Error ? error.message : String(error),
      });
    } finally {
      isFetching = false;
      if (isActive) schedule(nextDelayMs ?? resolveNextIntervalMs());
    }
  };

  /**
   * 画面復帰・回線復帰で取り直す
   * @returns {void}
   */
  const refreshOnResume = (): void => {
    void refresh();
  };

  /** 前回の予約までの残り。画面遷移で開き直した時は待ち、初回と期限切れは今すぐ取得する */
  const remainingMs = nextFetchAtMs - performance.now();
  if (remainingMs > 0) {
    schedule(remainingMs);
  } else {
    void refresh();
  }
  document.addEventListener('visibilitychange', refreshOnResume);
  window.addEventListener('online', refreshOnResume);

  return () => {
    isActive = false;
    clearTimeout(timer);
    document.removeEventListener('visibilitychange', refreshOnResume);
    window.removeEventListener('online', refreshOnResume);
  };
};

/**
 * 画面から掲示を購読する。既存UIテストの通信抑止は公開設定ストアと同じ
 * @param {() => void} listener - Reactの更新通知
 * @returns {() => void} 購読解除
 */
export const subscribePointCampaignBoard = (listener: () => void): (() => void) => {
  clearTimeout(stopTimer);
  listeners.add(listener);
  if (!stopSync && process.env.NODE_ENV !== 'test') stopSync = startPointCampaignBoardSync();
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0) {
      stopTimer = setTimeout(() => { stopSync?.(); stopSync = undefined; }, 0);
    }
  };
};

/**
 * テストの間で掲示と予約を消す
 * @returns {void}
 */
export const resetPointCampaignBoardForTests = (): void => {
  snapshot = EMPTY_SNAPSHOT;
  nextFetchAtMs = 0;
  unavailableUntilMs = 0;
};
